import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Loader2, Truck } from "lucide-react";

// ONE BOX FOR EVERYTHING A SEND IS MISSING (his rule, 3 Nov — the bin-scan and tag/photo questions looped).
// Every part, every gap — bin scan, tag, photo — one reason, one tap. The reason is logged on every part;
// a missing tag or photo still has to be put right before the job can be signed off.
export const SEND_REASONS = ["Locations not set up yet", "Bin label missing", "Tag printer down", "Photo on the way",
  "Truck is leaving now", "Customer waiting", "Urgent — build in progress"];

const slug = (r) => r.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 24).replace(/^-|-$/g, "");
const words = (m) => m.map((x) => `no ${x}`).join(" · ");

function Panel({ ask, busy, onSend, onCancel }) {
  const [reason, setReason] = useState("");
  const parts = ask.everything || [];
  const ready = reason.trim().length >= 3;
  const short = parts.some((p) => p.missing.some((m) => m !== "bin scan"));
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border-2 border-amber-400 bg-amber-50 p-4">
        <AlertTriangle className="w-7 h-7 shrink-0 text-amber-700" />
        <div>
          <div data-testid="ship-reason-title" className="font-black text-lg text-amber-950">
            {parts.length === 1 ? "This part is missing something" : `${parts.length} parts are missing something`}
          </div>
          <div className="mt-1 text-amber-900 text-base">It can still go — give one reason for all of it. Your name, the time and the reason go on every part.
            {short && <b> A missing tag or photo still has to be put right before the job can be signed off.</b>}</div>
        </div>
      </div>
      <ul data-testid="ship-reason-list" className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
        {parts.map((p) => (
          <li key={p.part_id} data-testid={`ship-reason-part-${p.short_code}`} className="px-3 py-2 text-sm flex flex-wrap gap-x-2">
            <b className="font-mono">{p.short_code}</b><span className="text-[#B01E28] font-semibold">{words(p.missing)}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {SEND_REASONS.map((r) => (
          <button key={r} type="button" data-testid={`ship-reason-preset-${slug(r)}`} onClick={() => setReason(r)}
            className={`rounded-full px-4 min-h-[44px] text-sm font-semibold border-2 transition-colors ${reason === r ? "bg-[#B01E28] text-white border-[#B01E28]" : "bg-white border-slate-300 hover:border-[#B01E28]"}`}>{r}</button>
        ))}
      </div>
      <Textarea data-testid="ship-reason-input" value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Or type a reason (3 letters or more)…" className="rounded-lg min-h-[60px] text-base" />
      <div className="grid gap-2">
        <button type="button" data-testid="ship-reason-confirm" disabled={!ready || busy} onClick={() => onSend(reason.trim())}
          className="w-full h-12 rounded-xl bg-[#B01E28] hover:bg-[#8f1820] px-5 font-black text-base text-white disabled:opacity-40 inline-flex items-center justify-center gap-2 transition-colors">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Truck className="w-5 h-5" />}{ready ? "Send — logged" : "Pick a reason first"}
        </button>
        <button type="button" data-testid="ship-reason-cancel" onClick={onCancel} disabled={busy}
          className="w-full h-12 rounded-xl border-2 border-slate-300 bg-white px-4 font-bold text-base text-slate-700 hover:border-slate-500 transition-colors">Cancel — I'll fix it first</button>
      </div>
    </div>
  );
}

export function SendReasonDialog({ ask, busy, onSend, onClose }) {
  return (
    <Dialog open={!!ask} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent data-testid="ship-reason-dialog" className="rounded-xl sm:max-w-lg max-h-kb overflow-y-auto">
        <DialogTitle className="sr-only">Send with what's missing</DialogTitle>
        <DialogDescription className="sr-only">One reason covers every missing bin scan, tag and photo.</DialogDescription>
        {ask && <Panel ask={ask} busy={busy} onSend={onSend} onCancel={onClose} />}
      </DialogContent>
    </Dialog>
  );
}
